import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import logoCalriz from "../../assets/logos/logo-oficial-calriz.png";
import LanguageSwitcher from "../LanguageSwitcher";
import ArrowButton from "../ArrowButton/ArrowButton";
import NavItemLink from "./NavItemLink";
import HeaderMenu from "./HeaderMenu";
import { useSolutionGroups } from "../../pages/Servicos/ServicesContent";
import { useCaseMenuGroups } from "../../pages/Cases/CasesContent";
import { useProdutoMenuGroups } from "../../pages/Produtos/ProdutosContent";
import { NavItem, useNavLinks } from "./useNavLinks";
import "./Header.css";

interface HeaderProps {
  children?: React.ReactNode;
  /** Landing com identidade própria (Universidade Calriz): troca a logo, os links e o botão, com o mesmo visual. */
  brand?: { logo: string; alt: string; to: string };
  links?: NavItem[];
  cta?: { label: string; to: string };
  /** Some ao rolar para baixo e volta ao rolar para cima. */
  autoHide?: boolean;
  /** Página só em português: sem o seletor de idioma. */
  hideLanguage?: boolean;
}

// Pílula branca flutuante. Retorna um Fragment (e não um wrapper) para o position: sticky
// funcionar em relação à página inteira. Os links institucionais (utility) ficam no menu mobile e no rodapé.
export default function Header({ children, brand, links, cta, autoHide, hideLanguage }: HeaderProps) {
  const { t } = useTranslation();
  const { main: siteLinks, utility } = useNavLinks();
  // links próprios são âncoras da página: sem destaque de rota ativa (todos apontam para o mesmo caminho)
  const main = links ?? siteLinks;
  const action = cta ?? { label: t("header.contact", "Fale Conosco"), to: "/#contato" };
  const solutionGroups = useSolutionGroups();
  const productGroups = useProdutoMenuGroups();
  const caseGroups = useCaseMenuGroups();
  // submenus do header: os grupos, o link para a página inteira e o rótulo do botão de expandir no menu mobile
  const menus = {
    solutions: {
      groups: solutionGroups,
      all: { label: t("header.solutions_menu.all", "Todas as soluções"), to: "/solucoes" },
      expand: t("header.solutions_menu.expand", "Ver soluções"),
    },
    products: {
      groups: productGroups,
      all: { label: t("header.products_menu.all", "Todos os produtos"), to: "/produtos" },
      expand: t("header.products_menu.expand", "Ver produtos"),
    },
    cases: {
      groups: caseGroups,
      all: { label: t("header.cases_menu.all", "Todos os cases"), to: "/cases" },
      expand: t("header.cases_menu.expand", "Ver cases"),
    },
  };
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMenu, setDrawerMenu] = useState<keyof typeof menus | null>(null);
  const closeDrawer = () => {
    setDrawerOpen(false);
    setDrawerMenu(null);
  };

  // autoHide: compara com a última posição e ignora movimentos pequenos; perto do topo fica sempre visível
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    if (!autoHide) return;
    let lastY = window.scrollY;
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const y = window.scrollY;
        if (y < 80) {
          setHidden(false);
          lastY = y;
          return;
        }
        if (Math.abs(y - lastY) < 6) return;
        setHidden(y > lastY);
        lastY = y;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(frame);
    };
  }, [autoHide]);

  useEffect(() => {
    if (!drawerOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setDrawerOpen(false);
      setDrawerMenu(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [drawerOpen]);

  return (
    <>
      <header className={`cz-header cz-scope${autoHide ? " cz-header--autohide" : ""}${hidden ? " is-hidden" : ""}`}>
        <div className="cz-header__inner">
          <Link to={brand?.to ?? "/"} className={`cz-header__logo${brand ? " cz-header__logo--crest" : ""}`}>
            <img src={brand?.logo ?? logoCalriz} alt={brand?.alt ?? "Calriz"} />
          </Link>

          <nav className="cz-header__nav" aria-label="Principal">
            {main.map((item) =>
              item.menu ? (
                <HeaderMenu key={item.label} item={item} groups={menus[item.menu].groups} all={menus[item.menu].all} />
              ) : (
                <NavItemLink key={item.label} item={item} className="cz-header__link" highlightActive={!links} />
              )
            )}
          </nav>

          <div className="cz-header__end">
            {!hideLanguage && <LanguageSwitcher dropdown />}
            <ArrowButton to={action.to} variant="pink" size="sm" className="cz-header__cta">
              {action.label}
            </ArrowButton>
          </div>

          <button
            type="button"
            className="cz-header__burger"
            aria-label={t("header.menu_open", "Abrir menu")}
            aria-expanded={drawerOpen}
            aria-controls="cz-drawer"
            onClick={() => setDrawerOpen(true)}
          >
            <Menu size={26} />
          </button>
        </div>
        {children}
      </header>

      {drawerOpen && (
        <div className="cz-drawer-overlay cz-scope" onClick={closeDrawer}>
          <div
            id="cz-drawer"
            className="cz-drawer"
            role="dialog"
            aria-modal="true"
            aria-label={t("header.menu_open", "Abrir menu")}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="cz-drawer__close"
              aria-label={t("header.menu_close", "Fechar menu")}
              onClick={closeDrawer}
              autoFocus
            >
              <X size={26} />
            </button>
            <nav className="cz-drawer__main" aria-label="Principal">
              {main.map((item) =>
                item.menu ? (
                  <div key={item.label} className="cz-drawer__group">
                    <div className="cz-drawer__row">
                      <NavItemLink item={item} className="cz-drawer__link" onClick={closeDrawer} />
                      <button
                        type="button"
                        className={`cz-drawer__expand${drawerMenu === item.menu ? " is-open" : ""}`}
                        aria-label={menus[item.menu].expand}
                        aria-expanded={drawerMenu === item.menu}
                        aria-controls={`cz-drawer-${item.menu}`}
                        onClick={() => setDrawerMenu((v) => (v === item.menu ? null : item.menu ?? null))}
                      >
                        <ChevronDown size={22} aria-hidden="true" />
                      </button>
                    </div>
                    {drawerMenu === item.menu && (
                      <ul id={`cz-drawer-${item.menu}`} className="cz-drawer__sublist">
                        {menus[item.menu].groups.map((group) => (
                          <li key={group.key}>
                            <Link to={group.to} className="cz-drawer__small-link" onClick={closeDrawer}>
                              {group.title}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : (
                  <NavItemLink key={item.label} item={item} className="cz-drawer__link" onClick={closeDrawer} />
                )
              )}
            </nav>
            {!links && (
              <nav className="cz-drawer__utility" aria-label="Institucional">
                {utility.map((item) => (
                  <NavItemLink key={item.label} item={item} className="cz-drawer__small-link" onClick={closeDrawer} />
                ))}
              </nav>
            )}
            {!hideLanguage && <LanguageSwitcher />}
            <ArrowButton to={action.to} variant="pink" size="md" icon={cta ? "arrow" : "external"} onClick={closeDrawer}>
              {action.label}
            </ArrowButton>
          </div>
        </div>
      )}
    </>
  );
}
